import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Table,
  Minus,
  Image,
  Link,
  CheckSquare,
  AlertCircle,
  Info,
  FileText,
  BookOpen,
  GitBranch,
  Calculator,
  Youtube,
} from 'lucide-react';

export interface SlashCommand {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  action: () => string;
}

interface SlashCommandMenuProps {
  visible: boolean;
  position: { top: number; left: number };
  filter: string;
  onSelect: (command: SlashCommand) => void;
  onClose: () => void;
}

const commands: SlashCommand[] = [
  // 标题
  {
    id: 'h1',
    title: '标题 1',
    description: '大标题',
    icon: <Heading1 className="h-4 w-4" />,
    category: '基础区块',
    action: () => '# ',
  },
  {
    id: 'h2',
    title: '标题 2',
    description: '中等标题',
    icon: <Heading2 className="h-4 w-4" />,
    category: '基础区块',
    action: () => '## ',
  },
  {
    id: 'h3',
    title: '标题 3',
    description: '小标题',
    icon: <Heading3 className="h-4 w-4" />,
    category: '基础区块',
    action: () => '### ',
  },
  {
    id: 'h4',
    title: '标题 4',
    description: '更小的标题',
    icon: <Heading3 className="h-4 w-4 text-sm" />,
    category: '基础区块',
    action: () => '#### ',
  },
  // 列表
  {
    id: 'bullet-list',
    title: '无序列表',
    description: '创建简单的无序列表',
    icon: <List className="h-4 w-4" />,
    category: '列表',
    action: () => '- ',
  },
  {
    id: 'numbered-list',
    title: '有序列表',
    description: '创建带编号的列表',
    icon: <ListOrdered className="h-4 w-4" />,
    category: '列表',
    action: () => '1. ',
  },
  {
    id: 'todo-list',
    title: '待办事项',
    description: '创建带复选框的待办列表',
    icon: <CheckSquare className="h-4 w-4" />,
    category: '列表',
    action: () => '- [ ] 待办事项\n- [x] 已完成事项\n',
  },
  // 内容块
  {
    id: 'quote',
    title: '引用',
    description: '插入引用内容',
    icon: <Quote className="h-4 w-4" />,
    category: '内容块',
    action: () => '> ',
  },
  {
    id: 'code',
    title: '代码块',
    description: '插入代码块',
    icon: <Code className="h-4 w-4" />,
    category: '内容块',
    action: () => '```javascript\n// 在此输入代码\n```\n',
  },
  {
    id: 'code-with-line-numbers',
    title: '代码块（带行号）',
    description: '插入带行号的代码块',
    icon: <Code className="h-4 w-4" />,
    category: '内容块',
    action: () => '```javascript showLineNumbers\n// 在此输入代码\n```\n',
  },
  {
    id: 'code-typescript',
    title: 'TypeScript 代码块',
    description: '插入 TypeScript 代码块',
    icon: <Code className="h-4 w-4 text-blue-500" />,
    category: '内容块',
    action: () => '```typescript\n// 在此输入代码\n```\n',
  },
  {
    id: 'code-python',
    title: 'Python 代码块',
    description: '插入 Python 代码块',
    icon: <Code className="h-4 w-4 text-green-500" />,
    category: '内容块',
    action: () => '```python\n# 在此输入代码\n```\n',
  },
  {
    id: 'code-bash',
    title: 'Shell/Bash 代码块',
    description: '插入 Shell 命令块',
    icon: <Code className="h-4 w-4 text-yellow-600" />,
    category: '内容块',
    action: () => '```bash\n# 在此输入命令\n```\n',
  },
  {
    id: 'code-json',
    title: 'JSON 代码块',
    description: '插入 JSON 数据块',
    icon: <Code className="h-4 w-4 text-orange-500" />,
    category: '内容块',
    action: () => '```json\n{\n  "key": "value"\n}\n```\n',
  },
  {
    id: 'code-html',
    title: 'HTML 代码块',
    description: '插入 HTML 代码块',
    icon: <Code className="h-4 w-4 text-red-500" />,
    category: '内容块',
    action: () => '```html\n<div>\n  <!-- HTML 代码 -->\n</div>\n```\n',
  },
  {
    id: 'code-css',
    title: 'CSS 代码块',
    description: '插入 CSS 样式块',
    icon: <Code className="h-4 w-4 text-purple-500" />,
    category: '内容块',
    action: () => '```css\n.selector {\n  property: value;\n}\n```\n',
  },
  {
    id: 'table',
    title: '表格',
    description: '插入表格',
    icon: <Table className="h-4 w-4" />,
    category: '内容块',
    action: () => '| 标题1 | 标题2 | 标题3 |\n|-------|-------|-------|\n| 内容1 | 内容2 | 内容3 |\n',
  },
  {
    id: 'divider',
    title: '分割线',
    description: '插入水平分割线',
    icon: <Minus className="h-4 w-4" />,
    category: '内容块',
    action: () => '\n---\n',
  },
  {
    id: 'callout-info',
    title: '信息提示',
    description: '插入信息提示框',
    icon: <Info className="h-4 w-4" />,
    category: '内容块',
    action: () => '> 💡 **提示**\n> 在此输入提示信息\n',
  },
  {
    id: 'callout-warning',
    title: '警告提示',
    description: '插入警告提示框',
    icon: <AlertCircle className="h-4 w-4" />,
    category: '内容块',
    action: () => '> ⚠️ **警告**\n> 在此输入警告信息\n',
  },
  // 媒体
  {
    id: 'link',
    title: '链接',
    description: '插入超链接',
    icon: <Link className="h-4 w-4" />,
    category: '媒体',
    action: () => '[链接文本](https://example.com)',
  },
  {
    id: 'image',
    title: '图片',
    description: '插入图片',
    icon: <Image className="h-4 w-4" />,
    category: '媒体',
    action: () => '![图片描述](https://example.com/image.png)',
  },
  {
    id: 'youtube',
    title: 'YouTube 视频',
    description: '嵌入 YouTube 视频',
    icon: <Youtube className="h-4 w-4" />,
    category: '媒体',
    action: () => '<iframe width="560" height="315" src="https://www.youtube.com/embed/VIDEO_ID" frameborder="0" allowfullscreen></iframe>\n',
  },
  {
    id: 'vimeo',
    title: 'Vimeo 视频',
    description: '嵌入 Vimeo 视频',
    icon: <Youtube className="h-4 w-4 text-blue-500" />,
    category: '媒体',
    action: () => '<iframe width="560" height="315" src="https://player.vimeo.com/video/VIDEO_ID" frameborder="0" allowfullscreen></iframe>\n',
  },
  {
    id: 'figma',
    title: 'Figma 设计',
    description: '嵌入 Figma 设计文件',
    icon: <Image className="h-4 w-4 text-purple-500" />,
    category: '媒体',
    action: () => '<iframe width="800" height="450" src="https://www.figma.com/embed?embed_host=share&url=FIGMA_URL" allowfullscreen></iframe>\n',
  },
  {
    id: 'codesandbox',
    title: 'CodeSandbox',
    description: '嵌入 CodeSandbox 代码演示',
    icon: <Code className="h-4 w-4 text-cyan-500" />,
    category: '媒体',
    action: () => '<iframe width="800" height="500" src="https://codesandbox.io/embed/SANDBOX_ID?fontsize=14&hidenavigation=1&theme=dark" allowfullscreen></iframe>\n',
  },
  {
    id: 'gist',
    title: 'GitHub Gist',
    description: '嵌入 GitHub Gist 代码片段',
    icon: <Code className="h-4 w-4 text-gray-600" />,
    category: '媒体',
    action: () => '<script src="https://gist.github.com/USERNAME/GIST_ID.js"></script>\n',
  },
  // 高级
  {
    id: 'mermaid',
    title: 'Mermaid 图表',
    description: '插入 Mermaid 流程图/时序图',
    icon: <GitBranch className="h-4 w-4" />,
    category: '高级',
    action: () => '```mermaid\ngraph TD\n    A[开始] --> B[结束]\n```\n',
  },
  {
    id: 'math',
    title: '数学公式',
    description: '插入 LaTeX 数学公式',
    icon: <Calculator className="h-4 w-4" />,
    category: '高级',
    action: () => '$$\nE = mc^2\n$$\n',
  },
  {
    id: 'wiki-link',
    title: 'Wiki 链接',
    description: '插入内部页面链接',
    icon: <BookOpen className="h-4 w-4" />,
    category: '高级',
    action: () => '[[页面名称]]',
  },
  {
    id: 'template',
    title: '模板',
    description: '插入常用文档模板',
    icon: <FileText className="h-4 w-4" />,
    category: '高级',
    action: () => '## 概述\n\n在此添加概述内容。\n\n## 详细内容\n\n### 背景\n\n### 目标\n\n### 方案\n\n## 参考\n\n- [链接1](url)\n- [链接2](url)\n',
  },
];

const categories = ['基础区块', '列表', '内容块', '媒体', '高级'];

export function SlashCommandMenu({ visible, position, filter, onSelect, onClose }: SlashCommandMenuProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  const filteredCommands = useMemo(() => {
    if (!filter) return commands;
    const lowerFilter = filter.toLowerCase();
    return commands.filter(
      (cmd) =>
        cmd.title.toLowerCase().includes(lowerFilter) ||
        cmd.description.toLowerCase().includes(lowerFilter) ||
        cmd.id.toLowerCase().includes(lowerFilter)
    );
  }, [filter]);

  // Reset selected index when filter changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [filter]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % filteredCommands.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + filteredCommands.length) % filteredCommands.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredCommands[selectedIndex]) {
            onSelect(filteredCommands[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, filteredCommands, selectedIndex, onSelect, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (menuRef.current) {
      const selectedItem = menuRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Handle click outside
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible, onClose]);

  if (!visible || filteredCommands.length === 0) {
    return null;
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-50 w-72 max-h-80 overflow-y-auto bg-popover border border-border rounded-lg shadow-lg"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <div className="p-2">
        <div className="text-xs text-muted-foreground px-2 py-1.5 font-medium">
          命令菜单 {filter && <span className="text-primary">- 搜索 "{filter}"</span>}
        </div>
        {categories.map((category) => {
          const categoryCommands = filteredCommands.filter((cmd) => cmd.category === category);
          if (categoryCommands.length === 0) return null;
          return (
            <div key={category} className="mt-2">
              <div className="text-xs text-muted-foreground px-2 py-1">{category}</div>
              {categoryCommands.map((cmd) => {
                const globalIndex = filteredCommands.indexOf(cmd);
                return (
                  <button
                    key={cmd.id}
                    data-index={globalIndex}
                    className={`w-full flex items-center gap-3 px-2 py-2 rounded-md text-left transition-colors ${
                      globalIndex === selectedIndex
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => onSelect(cmd)}
                    onMouseEnter={() => setSelectedIndex(globalIndex)}
                  >
                    <span
                      className={`flex-shrink-0 ${
                        globalIndex === selectedIndex ? 'text-primary-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      {cmd.icon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{cmd.title}</div>
                      <div
                        className={`text-xs truncate ${
                          globalIndex === selectedIndex ? 'text-primary-foreground/70' : 'text-muted-foreground'
                        }`}
                      >
                        {cmd.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="border-t px-2 py-1.5 text-xs text-muted-foreground flex items-center justify-between">
        <span>↑↓ 选择 • Enter 确认</span>
        <span>Esc 关闭</span>
      </div>
    </div>
  );
}

// Custom hook for slash command handling
export function useSlashCommand(
  editorRef: React.RefObject<any>,
  onInsert: (text: string) => void
) {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [filter, setFilter] = useState('');
  const slashPosRef = useRef<number | null>(null);

  const handleSelect = useCallback(
    (command: SlashCommand) => {
      const view = editorRef.current?.view;
      if (!view || slashPosRef.current === null) {
        // Fallback: just insert at cursor
        onInsert(command.action());
        setShowMenu(false);
        return;
      }

      // Get the text to insert
      const insertText = command.action();

      const currentPos = view.state.selection.main.head;

      // Delete from slash to current cursor (including filter text)
      view.dispatch({
        changes: {
          from: slashPosRef.current,
          to: currentPos,
          insert: insertText,
        },
        selection: {
          anchor: slashPosRef.current + insertText.length,
        },
      });

      view.focus();
      setShowMenu(false);
      slashPosRef.current = null;
    },
    [editorRef, onInsert]
  );

  const handleClose = useCallback(() => {
    setShowMenu(false);
    slashPosRef.current = null;
  }, []);

  // Key change handler to detect slash commands
  const handleEditorChange = useCallback((value: string, view: any) => {
    const selection = view?.state?.selection?.main;
    if (!selection) return value;

    const cursorPos = selection.head;
    const textBefore = value.slice(0, cursorPos);

    // Find if we're after a slash at the start of a line or after whitespace
    const slashMatch = textBefore.match(/(?:^|\n)\s*(\/)([^\/\n]*)$/);

    if (slashMatch) {
      const slashIndex = textBefore.lastIndexOf('/');
      const filterText = textBefore.slice(slashIndex + 1);

      // Get cursor coordinates
      const coords = view.coordsAtPos?.(cursorPos);
      if (coords) {
        setMenuPosition({
          top: coords.bottom + 5,
          left: coords.left,
        });
      }

      slashPosRef.current = slashIndex;
      setFilter(filterText);
      setShowMenu(true);
    } else if (showMenu) {
      // Check if we've moved away from the slash command
      if (!textBefore.slice(slashPosRef.current || 0).match(/^\/[^\/\n]*$/)) {
        setShowMenu(false);
        slashPosRef.current = null;
      }
    }

    return value;
  }, [showMenu]);

  return {
    showMenu,
    menuPosition,
    filter,
    handleSelect,
    handleClose,
    handleEditorChange,
  };
}

export { commands };
